import { getServiceOrigin } from '@/shared/utils/FetchV2.util';
import { hasLegacyDownloadExtension, opensInLegacySystemBrowser } from './legacyShellNavigation';

type SignedLinkRoute = {
  service: 'fiscalEngine' | 'communications';
  prefix: string;
  /** Cap the server puts on the decoded last segment, in UTF-8 bytes. */
  maxNameBytes: number;
};

const SIGNED_LINK_ROUTES: SignedLinkRoute[] = [
  { service: 'fiscalEngine', prefix: '/api/v1/heru-fiscal-engine/public/downloads/', maxNameBytes: 200 },
  {
    service: 'communications',
    prefix: '/api/v1/heru-communications-engine/publico/chat-media/',
    maxNameBytes: 255,
  },
];

const SHELL_EXTENSION = '.txt';
const FALLBACK_NAME = `archivo${SHELL_EXTENSION}`;

const parseUrl = (url: string): URL | null => {
  try {
    return new URL(url);
  } catch {
    return null;
  }
};

const utf8Size = (codePoint: number): number => {
  if (codePoint < 0x80) return 1;
  if (codePoint < 0x800) return 2;
  if (codePoint < 0x10000) return 3;
  return 4;
};

const utf8Length = (text: string): number => {
  let bytes = 0;
  for (const char of text) bytes += utf8Size(char.codePointAt(0) ?? 0);
  return bytes;
};

/** Cuts `text` to at most `maxBytes` of UTF-8 without splitting a character. */
const truncateUtf8 = (text: string, maxBytes: number): string => {
  let bytes = 0;
  let kept = '';
  for (const char of text) {
    bytes += utf8Size(char.codePointAt(0) ?? 0);
    if (bytes > maxBytes) break;
    kept += char;
  }
  return kept;
};

/**
 * The URL-escaped name with `.txt` appended. A name that fits keeps its escapes as
 * they came; one that would pass the cap loses the end of its base name first
 * (cut on the decoded text, then escaped again). `null` for a malformed escape.
 */
const appendShellExtension = (escapedName: string, maxNameBytes: number): string | null => {
  let name: string;
  try {
    name = decodeURIComponent(escapedName);
  } catch {
    return null;
  }
  if (utf8Length(name) + SHELL_EXTENSION.length <= maxNameBytes) return escapedName + SHELL_EXTENSION;
  const base = truncateUtf8(name, maxNameBytes - SHELL_EXTENSION.length);
  return encodeURIComponent(base) + SHELL_EXTENSION;
};

const isServiceOrigin = (service: SignedLinkRoute['service'], origin: string): boolean => {
  const serviceOrigin = parseUrl(getServiceOrigin(service));
  return serviceOrigin !== null && serviceOrigin.origin === origin;
};

/**
 * Our own public signed link (fiscal-engine download or chat attachment, on the gateway
 * host or the local service in dev) that the 1.4.0 shell would not hand to the system
 * browser, with `.txt` appended to its last segment. The token, query, fragment and
 * origin are untouched. `null` for any other URL or one that already ends in a
 * document extension.
 */
const rewriteSignedLink = (url: string): string | null => {
  if (hasLegacyDownloadExtension(url)) return null;
  const parsed = parseUrl(url);
  if (!parsed) return null;
  const route = SIGNED_LINK_ROUTES.find(
    ({ service, prefix }) => parsed.pathname.startsWith(prefix) && isServiceOrigin(service, parsed.origin),
  );
  if (!route) return null;

  const [token, name = '', ...extra] = parsed.pathname.slice(route.prefix.length).split('/');
  if (!token || extra.length > 0) return null;
  const lastSegment = name === '' ? FALLBACK_NAME : appendShellExtension(name, route.maxNameBytes);
  if (lastSegment === null) return null;
  return `${parsed.origin}${route.prefix}${token}/${lastSegment}${parsed.search}${parsed.hash}`;
};

/**
 * The URL to navigate to so an app binary without the bridge (1.4.0) opens it in the
 * system browser, or `null` when its shell would load it inside the WebView and nothing
 * here can change that (a third-party image on storage.googleapis.com, say).
 *
 * Why `.txt`: app releases are blocked, so every app user is on 1.4.0, whose shell hands
 * a navigation to the system browser only when the URL pathname ends in a document
 * extension. The last segment of our two signed links is cosmetic server-side (the
 * token decides authorization, the file, `Content-Type` and the `Content-Disposition`
 * name), so appending `.txt` to it makes photos, audio, video and the QR poster open
 * outside the WebView and still save under their real name.
 */
export const toLegacyBrowserUrl = (url: string): string | null =>
  rewriteSignedLink(url) ?? (opensInLegacySystemBrowser(url) ? url : null);
